import clsx from 'clsx'
import type { MouseEventHandler, PropsWithChildren } from 'react'
import React, { useEffect, useState } from 'react'
import Alert from '../Alert/Alert'
import { AriaLiveRole } from '../utilities/aria'

interface SnackbarProps {
  /**
   * The anchor position of the snackbar on the screen
   */
  anchorOrigin: AnchorOrigin
  /**
   * The number of milliseconds to wait before automatically hiding the snackbar
   */
  autoHideDuration?: number
  /**
   * Whether the snackbar is currently open/visible
   * @default true
   */
  open?: boolean
  /**
   * [ARIA live region](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Guides/Live_regions) role for accessibility.
   * Use `alert` for important, time-sensitive information, or `status` for advisory information
   * @default AriaLiveRole.Alert
   */
  role?: AriaLiveRole.Alert | AriaLiveRole.Status
  /**
   * The severity level that determines the color scheme and default icon
   * @default 'success'
   */
  severity?: Severity
}

type Severity = 'error' | 'info' | 'success' | 'warning'

type HorizontalAchorOrigin = 'center' | 'left' | 'right'

type VerticalAnchorOrigin = 'bottom' | 'center' | 'top'

type AnchorOrigin = {
  horizontal: HorizontalAchorOrigin
  vertical: VerticalAnchorOrigin
}

function Snackbar({
  anchorOrigin: { horizontal, vertical },
  autoHideDuration,
  children,
  open = true,
  role = AriaLiveRole.Alert,
  severity = 'success',
}: PropsWithChildren<SnackbarProps>): JSX.Element | null {
  const [visible, setVisible] = useState(() => open)

  useEffect(() => {
    if (autoHideDuration !== undefined) {
      setTimeout(() => setVisible(false), autoHideDuration)
    }
  }, [autoHideDuration])

  useEffect(() => {
    setVisible(open)
  }, [open])

  return visible ? (
    <div
      className={clsx(
        'fixed flex shadow-6 rounded-md w-full max-w-xs z-9999',
        horizontal === 'left'
          ? 'left-0 ml-4 md:ml-8'
          : horizontal === 'right'
          ? 'right-0 mr-4 md:mr-8'
          : 'left-half',
        vertical === 'bottom'
          ? 'bottom-0 mb-4 md:mb-8'
          : vertical === 'top'
          ? 'top-0 mt-4 md:mt-8'
          : 'top-half',
      )}
    >
      <Alert role={role} severity={severity}>
        {children}
      </Alert>
    </div>
  ) : null
}

function Content({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return <Alert.Content>{children}</Alert.Content>
}

function Description({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return <Alert.Description>{children}</Alert.Description>
}

function Title({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return <Alert.Title>{children}</Alert.Title>
}

function Actions({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return <Alert.Actions>{children}</Alert.Actions>
}

interface ButtonProps {
  /**
   * Custom data attributes to be added to the button element
   */
  dataSet?: Record<string, string | undefined>
  onClick?: MouseEventHandler<HTMLButtonElement>
}

function Button({
  children,
  dataSet,
  onClick,
}: PropsWithChildren<ButtonProps>): JSX.Element {
  return (
    <Alert.Button dataSet={dataSet} onClick={onClick}>
      {children}
    </Alert.Button>
  )
}

Snackbar.Actions = Actions
Snackbar.Button = Button
Snackbar.Content = Content
Snackbar.Description = Description
Snackbar.Title = Title

export default Snackbar
